/**
 * "Sign in with SaleFish" from another app (Showroom first): which sign-in
 * this tab is in the middle of.
 *
 * The app sends the person to /login?app=<uid> (server/oidc/routes.ts). They
 * may then leave this origin and come back (Google, Microsoft), or finish on
 * this page (password, passkey, the second factor) — so the uid is kept in
 * sessionStorage, which belongs to this tab and survives those round trips.
 * AuthContext spends it in its fully-signed-in branch, the one place every way
 * in passes through, and sends the tab to the hand-off route with a real page
 * load. The server decides everything that matters; this only says where to go.
 */

const KEY = "salefish.appSignIn";
/** Matches the provider's interaction lifetime (server/oidc/provider.ts). */
const LIFETIME_MS = 30 * 60 * 1000;
/** oidc-provider's interaction uids: url-safe, a few dozen characters. */
const UID = /^[A-Za-z0-9_-]{8,128}$/;

export function rememberAppSignIn(uid: string, now = Date.now()): void {
  if (!UID.test(uid)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ uid, at: now }));
  } catch {
    /* storage off: the sign-in lands in Console, and the app can be tried again */
  }
}

export function peekAppSignIn(now = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const { uid, at } = JSON.parse(raw) as { uid?: unknown; at?: unknown };
    if (typeof uid !== "string" || !UID.test(uid) || typeof at !== "number" || now - at > LIFETIME_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return uid;
  } catch {
    return null;
  }
}

export function clearAppSignIn(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Spend it: the uid, once, or null. */
export function takeAppSignIn(now = Date.now()): string | null {
  const uid = peekAppSignIn(now);
  if (uid) clearAppSignIn();
  return uid;
}

export const appSignInFinishPath = (uid: string) => `/api/oidc/interaction/${encodeURIComponent(uid)}/finish`;
export const appSignInAbortPath = (uid: string) => `/api/oidc/interaction/${encodeURIComponent(uid)}/abort`;
export const appSignInAboutPath = (uid: string) => `/api/oidc/interaction/${encodeURIComponent(uid)}/about`;
